/**
 * Sjekk ansattnumre mot PowerOffice (#183, owner 2026-10-19): the administrator uploads Go's own
 * employee list (Lønn → Ansatte, exported as CSV) and sees each person whose number in Timefly is
 * not the one in PowerOffice, with the server's suggestion and its basis (the same matching as Koble
 * ansatte). Needs no API link. What is ticked is saved as the person's Lønns-ID in this company.
 */
import { errorText, post, put } from '@/api/client';
import { T } from '@/i18n';
import { Button, Chip, Panel, toast } from '@/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { BASIS } from './PowerOfficeMatch';

interface Person {
  user_id: number;
  name: string;
  current: string | null;
  employee_id: string | null;
  suggestion: string | null;
  basis: keyof typeof BASIS | null;
  sure: boolean;
}
interface Checked {
  employees: { number: string; name: string }[];
  people: Person[];
}
const key = (v: string | null | undefined) => {
  const t = String(v ?? '').trim();
  return /^\d+$/.test(t) ? String(Number(t)) : t;
};
/** the number the file carries today: the Lønns-ID, else Ansattnr */
const now = (p: Person) => key(p.current) || key(p.employee_id);

export function PowerOfficeFileCheck() {
  const [csv, setCsv] = useState<string | null>(null);
  const [res, setRes] = useState<Checked | null>(null);
  const [pick, setPick] = useState<Record<number, string>>({});
  const read = useMutation({
    mutationFn: (text: string) => post<Checked>('/admin/poweroffice/match-file', { csv: text }),
    onSuccess: (r) => {
      setRes(r);
      // a sure suggestion that differs is ticked; a name-only one waits for the administrator
      setPick(
        Object.fromEntries(
          r.people
            .filter((p) => p.sure && p.suggestion && key(p.suggestion) !== now(p))
            .map((p) => [p.user_id, p.suggestion as string]),
        ),
      );
    },
    onError: (e) => toast(errorText(e)),
  });
  const save = useMutation({
    mutationFn: () =>
      put<Checked>('/admin/poweroffice/match-file', {
        csv,
        people: Object.entries(pick).map(([user_id, number]) => ({ user_id: Number(user_id), number })),
      }),
    onSuccess: (r) => {
      setRes(r);
      setPick({});
      toast(T('Ansattnumrene er lagret som Lønns-ID.'));
    },
    onError: (e) => toast(errorText(e)),
  });
  const off = res ? res.people.filter((p) => !p.suggestion || key(p.suggestion) !== now(p)) : [];
  const right = res ? res.people.length - off.length : 0;
  const n = Object.keys(pick).length;
  return (
    <Panel
      title={T('Sjekk ansattnumre mot PowerOffice')}
      sub={T(
        'Last ned ansattlisten i PowerOffice Go (Lønn, Ansatte) som CSV og last den opp her. Timefly viser hvem som har et annet nummer enn i PowerOffice.',
      )}
    >
      <div style={{ display: 'grid', gap: 10 }} data-testid="po-file-check">
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label={T('Ansattlisten fra PowerOffice')}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            const text = await f.text();
            setCsv(text);
            read.mutate(text);
          }}
        />
        {read.isPending && <output>{T('Leser listen …')}</output>}
        {res && (
          <>
            <p style={{ margin: 0, fontSize: 13 }} data-testid="po-file-summary">
              {T('{right} har riktig nummer · {off} å se på · {go} ansatte i PowerOffice', {
                right,
                off: off.length,
                go: res.employees.length,
              })}
            </p>
            {off.length > 0 && (
              <table className="t">
                <thead>
                  <tr>
                    <th />
                    <th>{T('Ansatt i Timefly')}</th>
                    <th>{T('Nå')}</th>
                    <th>{T('I PowerOffice')}</th>
                    <th>{T('Grunnlag')}</th>
                  </tr>
                </thead>
                <tbody>
                  {off.map((p) => (
                    <tr key={p.user_id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={T('Bruk nummeret fra PowerOffice for {name}', { name: p.name })}
                          disabled={!p.suggestion}
                          checked={pick[p.user_id] != null}
                          onChange={(e) => {
                            const next = { ...pick };
                            if (e.target.checked && p.suggestion) next[p.user_id] = p.suggestion;
                            else delete next[p.user_id];
                            setPick(next);
                          }}
                        />
                      </td>
                      <td>{p.name}</td>
                      <td>{now(p) || '–'}</td>
                      <td>{p.suggestion ?? T('Ikke funnet')}</td>
                      <td>
                        {p.basis ? (
                          <Chip tone={BASIS[p.basis][1]} small>
                            {T(BASIS[p.basis][0])}
                          </Chip>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <span>
              <Button tone="pri" onClick={() => save.mutate()} disabled={save.isPending || n === 0}>
                {save.isPending ? T('Lagrer…') : T('Lagre {n} nummer', { n })}
              </Button>
            </span>
          </>
        )}
      </div>
    </Panel>
  );
}
